import { useMemo, useState } from 'react';
import {
  Plus, Search, BookOpen, Sparkles, Pencil, MoreVertical, AlertCircle,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { can } from '@/types';
import {
  useAccounts, useSeedChartOfAccounts,
} from '@/hooks/useAccounting';
import {
  ACCOUNT_TYPE_LABELS, ACCOUNT_TYPES,
  type Account, type AccountType,
} from '@/types/accounting';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Card } from '@/components/ui/Card';
import { LoaderScreen } from '@/components/ui/LoaderScreen';
import { EmptyState } from '@/components/ui/EmptyState';
import { AccountTypeBadge } from '@/components/accounting/AccountTypeBadge';
import { AccountFormModal } from '@/components/accounting/AccountFormModal';
import { formatCurrency } from '@/lib/utils';

export function ChartOfAccountsPage() {
  const { user } = useAuth();
  const canWrite = user ? can(user.role, 'accounting', 'write') : false;

  const [search, setSearch] = useState('');
  const [type, setType] = useState<'' | AccountType>('');

  const { data, isLoading, error } = useAccounts();
  const seed = useSeedChartOfAccounts();

  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<Account | null>(null);
  const [openMenu, setOpenMenu] = useState<string | null>(null);

  const filtered = useMemo(() => {
    if (!data) return [];
    return data
      .filter((a) => a.active)
      .filter((a) => {
        if (type && a.type !== type) return false;
        if (search.trim()) {
          const s = search.toLowerCase();
          return `${a.number} ${a.name} ${a.subtype ?? ''} ${a.description ?? ''}`.toLowerCase().includes(s);
        }
        return true;
      })
      .sort((a, b) => a.number.localeCompare(b.number, undefined, { numeric: true }));
  }, [data, search, type]);

  const groupedByType = useMemo(() => {
    const groups: Record<AccountType, Account[]> = {
      asset: [], liability: [], equity: [], revenue: [], expense: [],
    };
    for (const a of filtered) (groups[a.type] ||= []).push(a);
    return groups;
  }, [filtered]);

  async function onSeed() {
    try {
      const result = await seed.mutateAsync();
      toast.success(`Seeded ${result.created} standard accounts`);
    } catch (err) {
      toast.error((err as { message?: string }).message ?? 'Seed failed.');
    }
  }

  if (isLoading) return <LoaderScreen />;
  if (error) {
    return (
      <Card>
        <EmptyState
          icon={AlertCircle}
          title="Could not load chart of accounts"
          description={(error as { message?: string })?.message ?? 'An unexpected error occurred.'}
        />
      </Card>
    );
  }

  // Empty state — offer to seed
  if (!data || data.length === 0) {
    return (
      <Card>
        <EmptyState
          icon={BookOpen}
          title="No chart of accounts yet"
          description="Start fresh or seed a standard manufacturing chart — 40 accounts covering assets, liabilities, equity, revenue, and expenses for a millwork business."
          action={
            canWrite ? (
              <div className="flex gap-3">
                <Button leftIcon={<Sparkles size={14} />} onClick={onSeed} loading={seed.isPending}>
                  Seed Standard Chart
                </Button>
                <Button variant="secondary" leftIcon={<Plus size={14} />} onClick={() => setCreateOpen(true)}>
                  Add Manually
                </Button>
              </div>
            ) : null
          }
        />
        <AccountFormModal open={createOpen} onClose={() => setCreateOpen(false)} />
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex-1 min-w-[16rem]">
          <Input
            placeholder="Search by number, name, subtype…"
            leftIcon={<Search size={14} />}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className="w-44">
          <Select
            value={type}
            onChange={(e) => setType(e.target.value as AccountType | '')}
            options={[
              { value: '', label: 'All types' },
              ...ACCOUNT_TYPES.map((t) => ({ value: t, label: ACCOUNT_TYPE_LABELS[t] })),
            ]}
          />
        </div>
        {canWrite && (
          <Button leftIcon={<Plus size={14} />} onClick={() => setCreateOpen(true)}>
            New Account
          </Button>
        )}
      </div>

      {/* Grouped list */}
      {filtered.length === 0 ? (
        <Card>
          <EmptyState icon={BookOpen} title="No accounts match your filters" description="Try clearing some filters." />
        </Card>
      ) : (
        <div className="space-y-6">
          {ACCOUNT_TYPES.map((accType) => {
            const rows = groupedByType[accType];
            if (!rows || rows.length === 0) return null;
            const groupTotal = rows.reduce((s, a) => s + (a.balance ?? 0), 0);
            return (
              <Card key={accType} className="p-0 overflow-hidden">
                <header className="flex items-baseline justify-between px-6 py-4 border-b border-border bg-bg-subtle">
                  <div className="flex items-baseline gap-3">
                    <AccountTypeBadge type={accType} />
                    <span className="text-sm text-text-muted">{rows.length} account{rows.length === 1 ? '' : 's'}</span>
                  </div>
                  <div className="tabular text-sm">
                    <span className="kicker mr-3">Total</span>
                    <span className="font-display text-lg text-brass-300">{formatCurrency(groupTotal)}</span>
                  </div>
                </header>
                <table className="w-full text-sm">
                  <tbody>
                    {rows.map((a) => (
                      <tr key={a.id} className="border-b border-border last:border-0 hover:bg-bg-hover/40 group">
                        <td className="px-6 py-3 font-mono text-brass-300 w-28">{a.number}</td>
                        <td className="px-6 py-3">
                          <div className="text-text">{a.name}</div>
                          {a.subtype && <div className="text-2xs text-text-dim">{a.subtype}</div>}
                        </td>
                        <td className="px-6 py-3 text-right tabular text-text">
                          {a.balance !== undefined ? formatCurrency(a.balance) : <span className="text-text-dim">—</span>}
                        </td>
                        <td className="px-6 py-3 text-right w-12 relative">
                          {canWrite && (
                            <>
                              <button
                                onClick={() => setOpenMenu(openMenu === a.id ? null : a.id)}
                                className="p-1.5 text-text-dim hover:text-text hover:bg-bg-hover rounded-sm transition-colors opacity-0 group-hover:opacity-100"
                                aria-label="Actions"
                              >
                                <MoreVertical size={15} />
                              </button>
                              {openMenu === a.id && (
                                <>
                                  <div className="fixed inset-0 z-10" onClick={() => setOpenMenu(null)} />
                                  <div className="absolute right-4 top-10 z-20 w-36 bg-bg-raised border border-border-strong rounded-sm shadow-raised py-1 animate-fade-in">
                                    <button onClick={() => { setEditing(a); setOpenMenu(null); }}
                                      className="w-full flex items-center gap-2.5 px-3 py-2 text-xs text-text hover:bg-bg-hover text-left">
                                      <Pencil size={13} /> Edit
                                    </button>
                                  </div>
                                </>
                              )}
                            </>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Card>
            );
          })}
        </div>
      )}

      <AccountFormModal open={createOpen} onClose={() => setCreateOpen(false)} />
      <AccountFormModal open={!!editing} onClose={() => setEditing(null)} account={editing} />
    </div>
  );
}
